<template>
    <main class="ai-chat-page">
        <section class="ai-chat-shell">
            <header class="ai-chat-header">
                <div>
                    <p>AI Assistant</p>
                    <h1>AI对话</h1>
                </div>
                <span class="chat-status" :class="{ active: sendFlag }">
                    {{ sendFlag ? "思考中" : "在线" }}
                </span>
            </header>

            <div ref="contentRef" class="chat-content">
                <div v-if="messageList.length === 0" class="empty-chat">
                    <img src="@/assets/images/robot.png" alt="">
                    <h2>今天想聊点什么？</h2>
                    <p>可以问我 Bot 代码思路、对战策略、项目功能设计，或者让它帮你整理一段说明。</p>
                </div>

                <div v-for="item in messageList" :key="item.id" class="message-row" :class="item.user">
                    <img class="message-avatar" :src="item.user === 'visitor' ? avatarSrc : robotAvatar"
                        :alt="item.user === 'visitor' ? '用户头像' : 'AI头像'">
                    <div class="message-block">
                        <div class="message-name">{{ item.user === 'visitor' ? username : 'AI助手' }}</div>
                        <div class="message-bubble markdown"
                            v-html="formatMessage(item)">
                        </div>
                        <div v-if="item.loading" class="message-bubble loading-bubble">
                            正在思考
                            <span v-if="item.loading" class="thinking-ellipsis" aria-hidden="true">
                                <span>.</span><span>.</span><span>.</span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <form class="chat-input-bar" @submit.prevent="sendMessage">
                <textarea v-model="sendData" placeholder="请输入你的问题..." :disabled="sendFlag" rows="1"
                    @keydown="handleInputKeydown"></textarea>
                <button class="btn btn-primary" type="submit" :disabled="sendFlag">
                    {{ sendFlag ? "发送中" : "发送" }}
                </button>
            </form>
        </section>
    </main>
</template>

<script>
import { computed, nextTick, ref } from 'vue';
import { useStore } from 'vuex';
import robotAvatar from '@/assets/images/robot.png';
import defaultAvatar from '@/assets/images/visitor.png';
import { apiUrl } from '@/config/backend';

export default {
    setup() {
        const store = useStore();
        const sendData = ref('');
        const messageList = ref([]);
        const sendFlag = ref(false);
        const contentRef = ref(null);

        const username = computed(() => store.state.user.username || "我");
        const avatarSrc = computed(() => store.state.user.photo || defaultAvatar);

        const scrollToBottom = async () => {
            await nextTick();
            if (contentRef.value) {
                contentRef.value.scrollTop = contentRef.value.scrollHeight;
            }
        };

        const pushMessage = (message) => {
            messageList.value.push({
                id: Date.now() + Math.random(),
                ...message,
            });
            scrollToBottom();
        };

        const escapeHtml = (value) => String(value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');

        const renderInlineMarkdown = (value) => escapeHtml(value)
            .replace(/`([^`]+)`/g, '<code>$1</code>')
            .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
            .replace(/\*([^*]+)\*/g, '<em>$1</em>');

        const isTableRow = (line) => {
            const value = line.trim();
            return value.startsWith('|') && value.endsWith('|') && value.split('|').length >= 4;
        };

        const isTableSeparator = (line) => {
            if (!isTableRow(line || '')) {
                return false;
            }
            return splitTableRow(line).every(cell => /^:?-{3,}:?$/.test(cell.trim()));
        };

        const splitTableRow = (line) => line
            .trim()
            .replace(/^\|/, '')
            .replace(/\|$/, '')
            .split('|')
            .map(cell => cell.trim());

        const renderMarkdown = (value) => {
            const lines = String(value || '').split('\n');
            const html = [];
            let inCode = false;
            let codeBuffer = [];
            let inList = false;
            let inTable = false;

            const closeList = () => {
                if (inList) {
                    html.push('</ul>');
                    inList = false;
                }
            };

            const closeTable = () => {
                if (inTable) {
                    html.push('</tbody></table></div>');
                    inTable = false;
                }
            };

            const closeCode = () => {
                if (inCode) {
                    html.push(`<pre><code>${escapeHtml(codeBuffer.join('\n'))}</code></pre>`);
                    codeBuffer = [];
                    inCode = false;
                }
            };

            lines.forEach((line, index) => {
                if (line.trim().startsWith('```')) {
                    if (inCode) {
                        closeCode();
                    } else {
                        closeList();
                        closeTable();
                        inCode = true;
                        codeBuffer = [];
                    }
                    return;
                }

                if (inCode) {
                    codeBuffer.push(line);
                    return;
                }

                if (isTableSeparator(line)) {
                    return;
                }

                if (isTableRow(line) && isTableSeparator(lines[index + 1] || '')) {
                    closeList();
                    closeTable();
                    inTable = true;
                    html.push('<div class="md-table-wrap"><table><thead><tr>');
                    splitTableRow(line).forEach(cell => {
                        html.push(`<th>${renderInlineMarkdown(cell)}</th>`);
                    });
                    html.push('</tr></thead><tbody>');
                    return;
                }

                if (inTable && isTableRow(line)) {
                    html.push('<tr>');
                    splitTableRow(line).forEach(cell => {
                        html.push(`<td>${renderInlineMarkdown(cell)}</td>`);
                    });
                    html.push('</tr>');
                    return;
                }

                if (inTable) {
                    closeTable();
                }

                const heading = line.match(/^(#{1,3})\s+(.+)$/);
                if (heading) {
                    closeList();
                    const level = heading[1].length + 2;
                    html.push(`<h${level}>${renderInlineMarkdown(heading[2])}</h${level}>`);
                    return;
                }

                const listItem = line.match(/^\s*[-*]\s+(.+)$/);
                if (listItem) {
                    if (!inList) {
                        html.push('<ul>');
                        inList = true;
                    }
                    html.push(`<li>${renderInlineMarkdown(listItem[1])}</li>`);
                    return;
                }

                closeList();
                if (line.trim() === '') {
                    html.push('<br>');
                } else {
                    html.push(`<p>${renderInlineMarkdown(line)}</p>`);
                }
            });

            closeCode();
            closeList();
            closeTable();
            return html.join('');
        };

        const formatMessage = (item) => {
            if (item.loading) {
                return '';
            }
            return renderMarkdown(item.content);
        };

        const sendMessage = async () => {
            const content = sendData.value.trim();
            if (!content) {
                pushMessage({
                    user: 'robot',
                    content: '你想让我回答什么呢？先输入一个问题吧。',
                });
                return;
            }

            pushMessage({
                user: 'visitor',
                content,
            });
            sendData.value = '';
            sendFlag.value = true;

            const loadingMessage = {
                id: Date.now() + Math.random(),
                user: 'robot',
                content: '正在思考',
                loading: true,
            };
            messageList.value.push(loadingMessage);
            scrollToBottom();

            try {
                const history = messageList.value
                    .filter(item => !item.loading)
                    .slice(0, -1)
                    .slice(-12)
                    .map(item => ({
                        role: item.user === 'visitor' ? 'user' : 'assistant',
                        content: item.content,
                    }));

                const response = await fetch(apiUrl('/api/ai/chat/'), {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${store.state.user.token}`,
                    },
                    body: JSON.stringify({
                        message: content,
                        history,
                    }),
                });

                if (!response.ok) {
                    throw new Error(`AI request failed: ${response.status}`);
                }

                const data = await response.json();
                loadingMessage.loading = false;
                loadingMessage.content = data.error_message === 'success'
                    ? data.answer
                    : data.answer || 'AI 服务暂时不可用，请稍后再试。';
            } catch (error) {
                loadingMessage.loading = false;
                loadingMessage.content = '请求失败，请稍后再试。';
            } finally {
                sendFlag.value = false;
                scrollToBottom();
            }
        };

        const handleInputKeydown = (event) => {
            if (event.key !== 'Enter') {
                return;
            }
            if (event.ctrlKey || event.metaKey) {
                event.preventDefault();
                sendMessage();
            }
        };

        return {
            sendData,
            messageList,
            sendFlag,
            sendMessage,
            handleInputKeydown,
            formatMessage,
            contentRef,
            username,
            avatarSrc,
            robotAvatar,
        };
    },
};
</script>

<style scoped>
.ai-chat-page {
    min-height: calc(100vh - 86px);
    padding: 24px 16px 32px;
}

.ai-chat-shell {
    display: grid;
    grid-template-rows: auto minmax(0, 1fr) auto;
    width: min(920px, 100%);
    height: calc(100vh - 142px);
    min-height: 560px;
    margin: 0 auto;
    overflow: hidden;
    background: color-mix(in srgb, var(--app-surface) 94%, transparent);
    border: 1px solid var(--app-border);
    border-radius: 18px;
    box-shadow: var(--app-shadow);
    backdrop-filter: blur(18px);
}

.ai-chat-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 20px 24px;
    background: var(--app-surface-muted);
    border-bottom: 1px solid var(--app-border);
}

.ai-chat-header p {
    margin: 0 0 4px;
    color: var(--app-muted);
    font-size: 0.78rem;
    font-weight: 800;
}

.ai-chat-header h1 {
    margin: 0;
    color: var(--app-text);
    font-size: 1.55rem;
    font-weight: 900;
}

.chat-status {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    color: var(--app-text);
    font-weight: 800;
    background: var(--app-surface-strong);
    border: 1px solid var(--app-border);
    border-radius: 999px;
}

.chat-status::before {
    content: "";
    width: 9px;
    height: 9px;
    background: #22c55e;
    border-radius: 50%;
    box-shadow: 0 0 12px rgba(34, 197, 94, 0.6);
}

.chat-status.active::before {
    background: var(--app-primary);
    animation: pulse 1s ease-in-out infinite;
}

.chat-content {
    overflow-y: auto;
    padding: 24px;
}

.empty-chat {
    display: grid;
    place-items: center;
    min-height: 100%;
    padding: 32px;
    text-align: center;
    color: var(--app-muted);
}

.empty-chat img {
    width: 78px;
    height: 78px;
    margin-bottom: 18px;
    border-radius: 22px;
    box-shadow: var(--app-shadow-soft);
}

.empty-chat h2 {
    margin: 0 0 10px;
    color: var(--app-text);
    font-size: 1.45rem;
    font-weight: 900;
}

.empty-chat p {
    max-width: 480px;
    margin: 0;
    line-height: 1.8;
}

.message-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 18px;
}

.message-row.visitor {
    flex-direction: row-reverse;
}

.message-avatar {
    width: 42px;
    height: 42px;
    object-fit: cover;
    border: 2px solid rgba(20, 184, 166, 0.28);
    border-radius: 50%;
}

.message-block {
    max-width: min(70%, 620px);
}

.message-row.visitor .message-block {
    text-align: right;
}

.message-name {
    margin-bottom: 5px;
    color: var(--app-muted);
    font-size: 0.8rem;
    font-weight: 800;
}

.message-bubble {
    display: inline-block;
    padding: 12px 14px;
    color: var(--app-text);
    line-height: 1.75;
    text-align: left;
    white-space: pre-wrap;
    background: var(--app-surface-strong);
    border: 1px solid var(--app-border);
    border-radius: 14px;
    box-shadow: var(--app-shadow-soft);
}

.loading-bubble {
    margin-top: 0;
}

.message-row.visitor .message-bubble {
    color: #ffffff;
    background: linear-gradient(135deg, var(--app-primary), #2563eb);
    border-color: transparent;
}

.message-bubble.markdown {
    max-width: 100%;
}

.message-bubble.markdown :deep(p) {
    margin: 0 0 10px;
}

.message-bubble.markdown :deep(p:last-child),
.message-bubble.markdown :deep(ul:last-child),
.message-bubble.markdown :deep(pre:last-child) {
    margin-bottom: 0;
}

.message-bubble.markdown :deep(h3),
.message-bubble.markdown :deep(h4),
.message-bubble.markdown :deep(h5) {
    margin: 12px 0 8px;
    color: var(--app-text);
    font-size: 1rem;
    font-weight: 900;
}

.message-bubble.markdown :deep(ul) {
    margin: 6px 0 10px;
    padding-left: 1.2rem;
}

.message-bubble.markdown :deep(li) {
    margin: 4px 0;
}

.message-bubble.markdown :deep(code) {
    padding: 2px 5px;
    color: #db2777;
    background: rgba(219, 39, 119, 0.1);
    border-radius: 5px;
    font-family: Consolas, Monaco, "Courier New", monospace;
    font-size: 0.92em;
}

.message-bubble.markdown :deep(pre) {
    max-width: 100%;
    margin: 10px 0;
    padding: 12px;
    overflow-x: auto;
    color: #e5e7eb;
    background: #111827;
    border-radius: 10px;
    white-space: pre;
}

.message-bubble.markdown :deep(pre code) {
    padding: 0;
    color: inherit;
    background: transparent;
    border-radius: 0;
}

.message-bubble.markdown :deep(.md-table-wrap) {
    max-width: 100%;
    margin: 10px 0;
    overflow-x: auto;
    border: 1px solid var(--app-border);
    border-radius: 10px;
}

.message-bubble.markdown :deep(table) {
    width: 100%;
    min-width: 520px;
    border-collapse: collapse;
    font-size: 0.95rem;
}

.message-bubble.markdown :deep(th),
.message-bubble.markdown :deep(td) {
    padding: 10px 12px;
    text-align: left;
    vertical-align: top;
    border-bottom: 1px solid var(--app-border);
    border-right: 1px solid var(--app-border);
}

.message-bubble.markdown :deep(th:last-child),
.message-bubble.markdown :deep(td:last-child) {
    border-right: 0;
}

.message-bubble.markdown :deep(tr:last-child td) {
    border-bottom: 0;
}

.message-bubble.markdown :deep(th) {
    color: var(--app-text);
    background: var(--app-surface-muted);
    font-weight: 900;
}

.thinking-ellipsis {
    display: inline-flex;
    align-items: flex-end;
    gap: 1px;
    margin-left: 2px;
    color: currentColor;
    vertical-align: bottom;
    white-space: nowrap;
}

.thinking-ellipsis span {
    display: inline-block;
    min-width: 0.28em;
    font-weight: 900;
    opacity: 0.35;
    will-change: transform, opacity;
    animation: ai-thinking-ellipsis 0.9s infinite ease-in-out;
}

.thinking-ellipsis span:nth-child(2) {
    animation-delay: 0.16s;
}

.thinking-ellipsis span:nth-child(3) {
    animation-delay: 0.32s;
}

.chat-input-bar {
    display: flex;
    gap: 10px;
    padding: 16px 18px;
    background: var(--app-surface-muted);
    border-top: 1px solid var(--app-border);
}

.chat-input-bar textarea {
    flex: 1;
    min-width: 0;
    min-height: 46px;
    max-height: 150px;
    padding: 11px 14px;
    color: var(--app-text);
    background: var(--app-surface-strong);
    border: 1px solid var(--app-border);
    border-radius: 12px;
    outline: none;
    line-height: 1.5;
    resize: vertical;
}

.chat-input-bar textarea:focus {
    border-color: rgba(20, 184, 166, 0.55);
    box-shadow: 0 0 0 3px rgba(20, 184, 166, 0.16);
}

.chat-input-bar .btn {
    min-width: 86px;
}

@keyframes ai-thinking-ellipsis {
    0%,
    80%,
    100% {
        transform: translateY(0);
        opacity: 0.35;
    }

    40% {
        transform: translateY(-5px);
        opacity: 1;
    }
}

@keyframes pulse {
    0%,
    100% {
        transform: scale(1);
    }

    50% {
        transform: scale(1.25);
    }
}

@media (max-width: 768px) {
    .ai-chat-page {
        padding: 12px;
    }

    .ai-chat-shell {
        height: calc(100vh - 112px);
        min-height: 520px;
        border-radius: 14px;
    }

    .ai-chat-header,
    .chat-content,
    .chat-input-bar {
        padding-left: 14px;
        padding-right: 14px;
    }

    .message-block {
        max-width: 82%;
    }
}
</style>
